import { useEffect, useRef, useState, type ReactNode } from "react";

// 手机端 tooltip：长按任何带 title 属性的元素 0.5s → 弹出自定义提示框
// 桌面端保留原生 title hover 行为，互不干扰
export default function TooltipProvider({ children }: { children: ReactNode }) {
  const [tip, setTip] = useState<{ text: string; x: number; y: number; key: number } | null>(null);
  const timerRef = useRef<number | null>(null);
  const keyRef = useRef(0);

  useEffect(() => {
    function clearTimer() {
      if (timerRef.current != null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }

    function findTitled(target: EventTarget | null): HTMLElement | null {
      const el = target as HTMLElement | null;
      if (!el) return null;
      return el.closest<HTMLElement>("[title]");
    }

    function onTouchStart(e: TouchEvent) {
      const titled = findTitled(e.target);
      if (!titled || !titled.title) return;
      const touch = e.touches[0];
      const text = titled.title;
      const x = touch.clientX;
      const y = touch.clientY;
      // 长按 500ms 弹出提示
      clearTimer();
      timerRef.current = window.setTimeout(() => {
        setTip({ text, x, y, key: ++keyRef.current });
        // 阻止后续 click，避免同时触发出牌等操作
        titled.dataset.tooltipActive = "1";
      }, 500);
    }

    function onTouchMove() {
      // 滚动/滑动时取消
      clearTimer();
    }

    function onTouchEnd(e: TouchEvent) {
      clearTimer();
      // 如果刚刚弹了 tooltip，阻止本次 click
      const titled = findTitled(e.target);
      if (titled && titled.dataset.tooltipActive) {
        delete titled.dataset.tooltipActive;
        e.preventDefault();
        e.stopPropagation();
      }
    }

    // 点击空白处或 tooltip 自身关闭
    function onDismiss() {
      setTip(null);
    }

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: true });
    document.addEventListener("touchend", onTouchEnd, { capture: true });
    document.addEventListener("click", onDismiss, true);

    return () => {
      clearTimer();
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd, true);
      document.removeEventListener("click", onDismiss, true);
    };
  }, []);

  if (!tip) return <>{children}</>;

  // 定位：尽量在触摸点上方，屏幕边界自动修正
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const maxW = Math.min(vw - 16, 300);
  let left = tip.x - maxW / 2;
  left = Math.max(8, Math.min(left, vw - maxW - 8));
  let top = tip.y - 16; // 默认在触摸点上方
  // 如果太靠上就放下方
  const placeBelow = top < 120;

  return (
    <>
      {children}
      {/* 遮罩：点击关闭 */}
      <div className="touch-tooltip-mask" onClick={() => setTip(null)}>
        <div
          key={tip.key}
          className="touch-tooltip"
          style={{
            left,
            top: placeBelow ? tip.y + 24 : undefined,
            bottom: placeBelow ? undefined : vh - top,
            maxWidth: maxW,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {tip.text}
        </div>
      </div>
    </>
  );
}
